import type { IDockviewPanelProps } from 'dockview-react';
import {
  CandlestickSeries,
  ColorType,
  createChart,
  HistogramSeries,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from 'lightweight-charts';
import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../lib/format';
import { type Candle, useChart, useConnectors } from './market';

export interface ChartParams {
  connector?: string;
  coin: string;
  interval: string;
}

const INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'];

const cssVar = (name: string, fallback: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

/**
 * Market chart tile (D10): TradingView Lightweight Charts v5 (Apache 2.0; the attribution logo stays on),
 * candles + volume from the connector's `get_chart`, refreshed every minute. Coin/interval live in the
 * panel params so they persist with the layout.
 */
export function ChartTile({ api, params }: IDockviewPanelProps<ChartParams>) {
  const connectors = useConnectors();
  const connector =
    params.connector ?? connectors.data?.connectors.find((c) => c.status === 'active')?.name;
  const [coin, setCoin] = useState(params.coin || 'ETH');
  const [draft, setDraft] = useState(coin);
  const interval = params.interval || '1h';
  const chart = useChart(connector, coin, interval);
  const el = useRef<HTMLDivElement>(null);
  const ref = useRef<{
    chart: IChartApi;
    candles: ISeriesApi<'Candlestick'>;
    volume: ISeriesApi<'Histogram'>;
  } | null>(null);

  useEffect(() => {
    api.setTitle(`${coin} · ${interval}`);
  }, [api, coin, interval]);

  useEffect(() => {
    const node = el.current;
    if (!node) return;
    const text = cssVar('--muted', '#9aa3b2');
    const border = cssVar('--border', '#2a2f3a');
    const c = createChart(node, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: text,
        attributionLogo: true,
      },
      grid: { vertLines: { color: border }, horzLines: { color: border } },
      rightPriceScale: { borderColor: border },
      timeScale: { borderColor: border, timeVisible: true, secondsVisible: false },
      crosshair: { mode: 0 },
    });
    const candles = c.addSeries(CandlestickSeries, {
      upColor: cssVar('--ok', '#3ecf8e'),
      downColor: cssVar('--danger', '#ff5c5c'),
      wickUpColor: cssVar('--ok', '#3ecf8e'),
      wickDownColor: cssVar('--danger', '#ff5c5c'),
      borderVisible: false,
    });
    const volume = c.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'vol',
      lastValueVisible: false,
      priceLineVisible: false,
      color: `${cssVar('--accent', '#4f8cff')}66`,
    });
    c.priceScale('vol').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    ref.current = { chart: c, candles, volume };
    return () => {
      c.remove();
      ref.current = null;
    };
  }, []);

  useEffect(() => {
    const r = ref.current;
    const data = chart.data?.candles;
    if (!r || !data) return;
    r.candles.setData(data.map(toBar));
    r.volume.setData(
      data.map((k) => ({
        time: (k.t / 1000) as UTCTimestamp,
        value: k.v,
        color: k.c >= k.o ? `${cssVar('--ok', '#3ecf8e')}55` : `${cssVar('--danger', '#ff5c5c')}55`,
      })),
    );
    r.chart.timeScale().fitContent();
  }, [chart.data]);

  const last = chart.data?.candles.at(-1);
  const first = chart.data?.candles[0];
  const change = last && first ? ((last.c - first.o) / first.o) * 100 : null;
  return (
    <div className="tile chart-tile">
      <div className="tile-bar">
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            const next = draft.trim().toUpperCase();
            if (!next) return;
            setCoin(next);
            api.updateParameters({ ...params, coin: next, interval });
          }}
        >
          <input
            aria-label="Coin"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{ width: 90 }}
          />
          <select
            aria-label="Interval"
            value={interval}
            onChange={(e) => api.updateParameters({ ...params, coin, interval: e.target.value })}
            style={{ width: 'auto' }}
          >
            {INTERVALS.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </form>
        <span className="grow" />
        {last && (
          <span className="mono">
            {last.c.toLocaleString(undefined, { maximumFractionDigits: 4 })}{' '}
            {change !== null && (
              <span className={change >= 0 ? 'ok' : 'error'}>
                {change >= 0 ? '+' : ''}
                {change.toFixed(2)}%
              </span>
            )}
          </span>
        )}
        <span className="muted" data-testid="chart-status">
          {!connector
            ? 'no connector'
            : chart.isLoading
              ? 'loading…'
              : chart.error
                ? errorMessage(chart.error)
                : `${chart.data?.candles.length ?? 0} candles`}
        </span>
      </div>
      <div ref={el} className="chart-canvas" />
    </div>
  );
}

const toBar = (k: Candle) => ({
  time: (k.t / 1000) as UTCTimestamp,
  open: k.o,
  high: k.h,
  low: k.l,
  close: k.c,
});
